import { useState } from 'react';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';

interface LogicalSpeaker {
  id: string;
  name: string;
  color: string;
}

interface InternalSpeaker {
  id: string;
  internalName: string;
  sessionNumber: number;
  displayName: string;
  logicalSpeakerId?: string;
  logicalSpeakerName?: string;
  logicalSpeakerColor?: string;
}

interface SpeakersPanelProps {
  logicalSpeakers: LogicalSpeaker[];
  internalSpeakers: InternalSpeaker[];
  editingSpeaker: string | null;
  editingName: string;
  onStartEditing: (speaker: LogicalSpeaker | InternalSpeaker) => void;
  onCancelEditing: () => void;
  onSaveSpeaker: (speakerId: string, name: string) => void;
  onEditingNameChange: (name: string) => void;
  deponent?: string | null;
  otherSpeakers?: string[];
}

const getSpeakerDisplayInfo = (speaker: LogicalSpeaker | InternalSpeaker) => {
  const isLogical = 'name' in speaker;
  return {
    name: isLogical ? speaker.name : speaker.displayName,
    color: isLogical ? speaker.color : speaker.logicalSpeakerColor || 'gray',
    isLogical,
  };
};

export const SpeakersPanel = ({
  logicalSpeakers,
  internalSpeakers,
  editingSpeaker,
  editingName,
  onStartEditing,
  onCancelEditing,
  onSaveSpeaker,
  onEditingNameChange,
  deponent,
  otherSpeakers,
}: SpeakersPanelProps) => {
  const [showCustomInput, setShowCustomInput] = useState<string | null>(null);

  const predefinedSpeakers = [
    ...(deponent ? [{ value: deponent, label: `${deponent} (Deponent)` }] : []),
    ...(otherSpeakers || []).map((speaker) => ({ value: speaker, label: speaker })),
  ];

  // Sort logical speakers with deponent first
  const sortedLogicalSpeakers = logicalSpeakers.toSorted((a) => (a.name === deponent ? -1 : 1));

  // Combine logical and internal speakers for display
  const allSpeakers = [
    ...sortedLogicalSpeakers.map((speaker) => ({ ...speaker, type: 'logical' as const })),
    ...internalSpeakers.map((speaker) => ({ ...speaker, type: 'internal' as const })),
  ];

  const handleCustomInputKeyDown = (speakerId: string, e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && editingName) {
      trackCustomEvent(TrackingEvents.AssignSpeaker);
      onSaveSpeaker(speakerId, editingName);
      setShowCustomInput(null);
    } else if (e.key === 'Escape') {
      setShowCustomInput(null);
      onCancelEditing();
    }
  };

  const handleCustomInputBlur = (speakerId: string) => {
    if (editingName) {
      trackCustomEvent(TrackingEvents.AssignSpeaker);
      onSaveSpeaker(speakerId, editingName);
    }
    setShowCustomInput(null);
    if (!editingName) {
      onCancelEditing();
    }
  };

  const handleSelectChange = (speaker: LogicalSpeaker | InternalSpeaker, value: string) => {
    if (value === '__custom__') {
      onStartEditing(speaker);
      setShowCustomInput(speaker.id);
      onEditingNameChange('');
      return;
    }

    const { name } = getSpeakerDisplayInfo(speaker);
    if (name !== value) {
      trackCustomEvent(TrackingEvents.AssignSpeaker);
      onStartEditing(speaker);
      onEditingNameChange(value);
      onSaveSpeaker(speaker.id, value);
    }
  };

  const handleInputChange = (speaker: LogicalSpeaker | InternalSpeaker, value: string) => {
    onStartEditing(speaker);
    onEditingNameChange(value);
  };

  return (
    <div className="scrollbar-thin scrollbar-track-transparent flex w-full items-center gap-2 overflow-x-auto border-b border-gray-200 bg-gray-100 px-4 py-3">
      <div className="flex-shrink-0 text-xs font-medium tracking-wide text-gray-500 uppercase">Speakers</div>
      {allSpeakers.toReversed().map((speaker) => {
        const { name, color } = getSpeakerDisplayInfo(speaker);

        const isEditing = editingSpeaker === speaker.id;
        const isCustomInput = showCustomInput === speaker.id;
        const currentValue = isEditing ? editingName : name;
        const isDeponent = name === deponent;

        return (
          <div
            key={speaker.id}
            className="group flex min-w-0 flex-shrink-0 items-center gap-1.5 rounded-full border bg-white px-2.5 py-1 shadow-sm transition-all hover:border-gray-300 hover:shadow-md"
          >
            {/* Dot */}
            <div className={`h-3 w-3 rounded-full bg-${color}-500 shadow-sm ring-1 ring-white`} />

            {isCustomInput ? (
              <input
                type="text"
                value={isEditing ? editingName : ''}
                onChange={(e) => handleInputChange(speaker, e.target.value)}
                onKeyDown={(e) => handleCustomInputKeyDown(speaker.id, e)}
                onBlur={() => handleCustomInputBlur(speaker.id)}
                className="w-20 rounded-md border-0 bg-gray-50 px-2 py-1 text-xs text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-500"
                placeholder="Enter name..."
                autoFocus
              />
            ) : (
              <select
                value={currentValue}
                onChange={(e) => handleSelectChange(speaker, e.target.value)}
                className="w-28 rounded-md border-0 bg-transparent px-1 py-0.5 text-xs text-gray-700 focus:bg-white focus:ring-2 focus:ring-blue-500"
              >
                <option value={name}>
                  {name}
                  {isDeponent && ' (Deponent)'}
                </option>
                {predefinedSpeakers.map((predefSpeaker) => (
                  <option key={predefSpeaker.value} value={predefSpeaker.value}>
                    {predefSpeaker.label}
                  </option>
                ))}
                <option value="__custom__">Enter custom name...</option>
              </select>
            )}
          </div>
        );
      })}
    </div>
  );
};
